import React from 'react';
import { motion } from 'framer-motion';
import { 
  FileArchive, 
  Search, 
  Cpu, 
  GitGraph, 
  FileText, 
  Database,
  ScanSearch,
  CheckCircle2,
  FileCode,
  AlertCircle
} from 'lucide-react';

// --- Visual Previews for Each Step ---

const PreviewIntake = () => (
  <div className="w-full h-24 bg-slate-950 rounded-lg border border-slate-800 relative overflow-hidden flex items-center justify-center">
    <motion.div
      initial={{ y: -30, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 1, repeat: Infinity, repeatDelay: 2 }}
      className="flex flex-col items-center"
    >
      <FileArchive className="text-blue-400 w-8 h-8 mb-1" />
      <span className="text-[10px] text-slate-500 font-mono">logs.tar.gz</span>
    </motion.div>
    <motion.div
      initial={{ width: 0 }}
      animate={{ width: "100%" }}
      transition={{ duration: 2, repeat: Infinity }}
      className="absolute bottom-0 left-0 h-1 bg-blue-500/50"
    />
  </div>
);

const PreviewUnzip = () => (
  <div className="w-full h-24 bg-slate-950 rounded-lg border border-slate-800 relative overflow-hidden flex items-center justify-center gap-2">
    <motion.div
      animate={{ scale: [1, 0.8, 0], opacity: [1, 1, 0] }}
      transition={{ duration: 2, repeat: Infinity, times: [0, 0.2, 0.3] }}
      className="absolute"
    >
      <FileArchive className="text-indigo-400 w-8 h-8" />
    </motion.div>
    
    {[0, 1, 2].map((i) => (
      <motion.div
        key={i}
        initial={{ y: 0, scale: 0, opacity: 0 }}
        animate={{ y: i % 2 === 0 ? -10 : 10, x: (i - 1) * 20, scale: 1, opacity: 1 }}
        transition={{ delay: 0.5 + i * 0.1, duration: 0.5, repeat: Infinity, repeatDelay: 1.2 }}
        className="bg-slate-800 p-1.5 rounded border border-slate-700"
      >
        <FileText className="w-4 h-4 text-indigo-300" />
      </motion.div>
    ))}
  </div>
);

const PreviewAnalysis = () => (
  <div className="w-full h-24 bg-slate-950 rounded-lg border border-slate-800 relative overflow-hidden p-2 font-mono text-[8px] leading-tight text-slate-500 flex flex-col gap-1">
    <div className="absolute inset-0 bg-gradient-to-b from-transparent via-cyan-900/10 to-transparent animate-pulse pointer-events-none" />
    <motion.div 
      animate={{ y: [-20, -100] }} 
      transition={{ duration: 5, repeat: Infinity, ease: "linear" }}
      className="flex flex-col gap-1"
    >
      {Array.from({ length: 12 }).map((_, i) => (
        <div key={i} className={`flex items-center gap-2 ${i === 4 ? 'bg-red-900/30 text-red-400 px-1 rounded' : ''}`}>
          <span className="opacity-50">12:0{i}</span>
          <span>{i === 4 ? "KERNEL PANIC: Null pointer" : "systemd[1]: Started Service..."}</span>
          {i === 4 && <AlertCircle className="w-3 h-3 ml-auto" />}
        </div>
      ))}
    </motion.div>
    <div className="absolute bottom-2 right-2 p-1 bg-cyan-950/80 border border-cyan-500/30 rounded text-cyan-400 text-[10px] flex items-center gap-1">
      <Cpu className="w-3 h-3" />
      <span>Thinking...</span>
    </div>
  </div>
);

const PreviewGit = () => (
  <div className="w-full h-24 bg-slate-950 rounded-lg border border-slate-800 relative overflow-hidden flex items-center justify-between px-4">
    <div className="flex flex-col items-center gap-1">
      <div className="text-[9px] text-red-400 border border-red-900/50 bg-red-950/30 px-2 py-1 rounded">
        Error at line 42
      </div>
    </div>
    <motion.div 
      animate={{ x: [0, 10, 0], opacity: [0.5, 1, 0.5] }}
      transition={{ duration: 1.5, repeat: Infinity }}
      className="h-[1px] w-8 bg-purple-500" 
    />
    <div className="flex flex-col items-center gap-1 border border-slate-700 bg-slate-900 p-2 rounded">
      <GitGraph className="w-5 h-5 text-purple-400" />
      <span className="text-[8px] text-slate-400">driver/net.c</span>
    </div>
  </div>
);

const PreviewReport = () => (
  <div className="w-full h-24 bg-slate-950 rounded-lg border border-slate-800 relative overflow-hidden flex flex-col p-3 gap-2">
    <div className="h-2 w-1/2 bg-emerald-500/20 rounded" />
    <div className="h-2 w-3/4 bg-slate-800 rounded" />
    <div className="h-2 w-full bg-slate-800 rounded" />
    <div className="mt-auto flex items-center gap-2 p-2 bg-emerald-950/30 border border-emerald-900/50 rounded">
      <CheckCircle2 className="w-4 h-4 text-emerald-500" />
      <span className="text-[10px] text-emerald-400">Root Cause Found</span>
    </div>
  </div>
);

const StepCard = ({ 
  icon: Icon, 
  title, 
  description, 
  color, 
  delay,
  PreviewComponent
}: { 
  icon: any, 
  title: string, 
  description: string, 
  color: string, 
  delay: number,
  PreviewComponent: React.FC
}) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.5 }}
      className={`relative p-5 rounded-xl border border-slate-700 bg-slate-800/50 backdrop-blur-sm w-72 flex-shrink-0 flex flex-col gap-4 group hover:border-${color}-500/50 transition-colors`}
    >
      <div className={`absolute -top-3 -left-3 w-10 h-10 rounded-full bg-slate-900 border border-${color}-500/30 flex items-center justify-center shadow-lg z-10`}>
        <Icon className={`w-5 h-5 text-${color}-400`} />
      </div>
      
      {/* Preview Area */}
      <div className="mt-2">
        <PreviewComponent />
      </div>

      <div>
        <h3 className="text-lg font-semibold text-slate-100">{title}</h3>
        <p className="text-xs text-slate-400 leading-relaxed mt-2">{description}</p>
      </div>

      <div className={`absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-${color}-500 to-transparent opacity-20`} />
    </motion.div>
  );
};

const ConnectingLine = ({ delay }: { delay: number }) => (
  <motion.div 
    initial={{ width: 0, opacity: 0 }}
    animate={{ width: 40, opacity: 1 }}
    transition={{ delay, duration: 0.8 }}
    className="h-[2px] bg-slate-700 relative hidden md:block flex-shrink-0"
  >
    <motion.div 
      animate={{ x: [0, 40] }}
      transition={{ repeat: Infinity, duration: 1.5, ease: "linear" }}
      className="absolute top-0 left-0 h-full w-10 bg-gradient-to-r from-transparent via-cyan-400 to-transparent opacity-50 blur-[2px]"
    />
  </motion.div>
);

export const ArchitectureDiagram: React.FC = () => {
  return (
    <div className="flex-1 w-full h-full bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-slate-900 via-slate-950 to-black relative overflow-y-auto p-10">
      <div className="w-full flex flex-col items-center px-6">
        
        <div className="text-center mb-16">
          <motion.h2 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-3xl font-bold text-white mb-4"
          >
            AI 驱动的 Linux 日志分析流程
          </motion.h2>
          <motion.p 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="text-slate-400 max-w-2xl mx-auto"
          >
            从原始压缩包到代码级根因定位的全自动化诊断链路
          </motion.p>
        </div>

        {/* Workflow Container */}
        <div className="flex flex-col md:flex-row items-start justify-center gap-0 md:gap-2 relative w-full overflow-x-auto pb-10">
          
          {/* Step 1: Input */}
          <StepCard 
            icon={FileArchive} 
            title="1. 数据摄入" 
            description="上传全系统日志压缩包 (.zip, .tar, .gz) 并描述观察到的故障症状。"
            color="blue"
            delay={0.1}
            PreviewComponent={PreviewIntake}
          />

          <div className="self-center">
             <ConnectingLine delay={0.4} />
          </div>

          {/* Step 2: Pre-Processing */}
          <StepCard 
            icon={Database} 
            title="2. 解压与提取" 
            description="解压归档文件。索引文件元数据。分离内核空间与用户空间日志。"
            color="indigo"
            delay={0.5}
            PreviewComponent={PreviewUnzip}
          />

          <div className="self-center">
            <ConnectingLine delay={0.8} />
          </div>

          {/* Step 3: AI Analysis Agent */}
          <div className="relative">
            <StepCard 
              icon={Cpu} 
              title="3. AI 推理分析" 
              description="智能体解析日志，按时间戳/模块过滤，并识别关键错误 (ERROR/PANIC) 特征。"
              color="cyan"
              delay={0.9}
              PreviewComponent={PreviewAnalysis}
            />
            {/* Satellite Nodes showing AI thinking */}
            <motion.div 
              animate={{ rotate: 360 }}
              transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
              className="absolute -inset-4 border border-dashed border-cyan-500/20 rounded-full z-[-1] hidden md:block"
            />
          </div>

          <div className="self-center">
            <ConnectingLine delay={1.2} />
          </div>

          {/* Step 4: Git Correlation */}
          <StepCard 
            icon={GitGraph} 
            title="4. 代码映射" 
            description="智能体在 Git 仓库中搜索堆栈跟踪 (Call Stack) 中提到的文件名或函数。"
            color="purple"
            delay={1.3}
            PreviewComponent={PreviewGit}
          />

          <div className="self-center">
            <ConnectingLine delay={1.6} />
          </div>

          {/* Step 5: Output */}
          <StepCard 
            icon={FileText} 
            title="5. 最终报告" 
            description="生成的 RCA 报告，包含相关日志片段、代码指针和修复建议。"
            color="emerald"
            delay={1.7}
            PreviewComponent={PreviewReport}
          />
        </div>

        {/* Legend / Details */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-8 w-full">
            <motion.div 
              initial={{ opacity: 0 }} 
              whileInView={{ opacity: 1 }} 
              className="bg-slate-900/50 p-6 rounded-lg border border-slate-800 hover:bg-slate-800/50 transition-colors"
            >
              <div className="flex items-center gap-3 mb-4">
                <ScanSearch className="text-cyan-400" />
                <h4 className="font-semibold text-slate-200">上下文过滤</h4>
              </div>
              <p className="text-sm text-slate-400">
                AI 不会读取每一行，而是根据用户的问题描述过滤相关的日志文件（例如："Wi-Fi 故障" &rarr; 聚焦 `wlan0`, `net/wireless`）。
              </p>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0 }} 
              whileInView={{ opacity: 1 }} 
              transition={{ delay: 0.1 }}
              className="bg-slate-900/50 p-6 rounded-lg border border-slate-800 hover:bg-slate-800/50 transition-colors"
            >
              <div className="flex items-center gap-3 mb-4">
                <Search className="text-purple-400" />
                <h4 className="font-semibold text-slate-200">错误模式匹配</h4>
              </div>
              <p className="text-sm text-slate-400">
                不仅识别显式的 "ERROR" 字符串，还能识别代码库中定义的超时、重传失败和异常状态转换等模式。
              </p>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0 }} 
              whileInView={{ opacity: 1 }} 
              transition={{ delay: 0.2 }}
              className="bg-slate-900/50 p-6 rounded-lg border border-slate-800 hover:bg-slate-800/50 transition-colors"
            >
              <div className="flex items-center gap-3 mb-4">
                <CheckCircle2 className="text-emerald-400" />
                <h4 className="font-semibold text-slate-200">可执行输出</h4>
              </div>
              <p className="text-sm text-slate-400">
                输出不仅仅是摘要；它是为开发者准备的简报，直接指向 Git 历史记录中的确切 C/C++ 文件和行号。
              </p>
            </motion.div>
        </div>

      </div>
    </div>
  );
};
